import Spinner from "@/components/chat/left-column/feedback/Spinner";
import { Button } from "@/components/primitives/button";
import { PHRASE_ENTROPY, PHRASE_WORDS_COUNT } from "@/config/constants";
import { toast } from "@/hooks/useToast";
import { copyTextToClipboard } from "@/lib/clipboard";
import { Copy, Download, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { FormStepProps } from "@/components/shared/Wizard/Wizard";
import { createMnemonicResource } from "@/utils/mnemonicResource";
import { ScrollArea } from "@/components/primitives/scroll-area";
import { ErrorBoundary, ErrorFallback } from "@/components/error-boundary";
import shield from "../../../assets/img/shield.svg";

interface WordsStepContainerProps {
  children: React.ReactNode;
  onNext?: () => void;
  onClose?: () => void;
  nextDisabled?: boolean;
}

function WordsStepContainer({
  children,
  onNext,
  onClose,
  nextDisabled,
}: WordsStepContainerProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col items-stretch justify-center gap-4">
      <h1 className="flex items-center self-start text-lg">
        {t("recover.wordsContainerTitle")}
      </h1>
      <p className="w-full font-light opacity-50">
        {t("recover.wordsContainerDescription")}
      </p>
      {children}
      <div className="flex items-center gap-2">
        <TriangleAlert className="size-5 text-destructive" />
        <p className="text-sm text-destructive">{t("recover.wordsAlert")}</p>
      </div>

      <p className="hidden w-full font-light opacity-50 md:block">
        {t("recover.wordsExplain")}
      </p>
      <div className="flex w-full items-center justify-end gap-4">
        {onClose && (
          <Button onClick={onClose} variant="outline">
            {t("cancel")}
          </Button>
        )}
        <Button onClick={onNext} disabled={nextDisabled}>
          {t("next")}
        </Button>
      </div>
    </div>
  );
}

interface WordsProps {
  words: string[];
}
function Words({ words }: WordsProps) {
  const { t } = useTranslation();
  async function handleCopy() {
    const copied = await copyTextToClipboard(words.join(" "));
    if (copied) {
      toast({
        duration: 1000,
        description: t("recover.copySuccess"),
        variant: "default",
      });
    }
  }
  function handleDownload() {
    const blob = new Blob([words.join(" ")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "recovery-words.txt";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }
  return (
    <div className="flex select-none flex-col items-center justify-center gap-2 bg-muted p-6">
      <img src={shield} alt="Shield Icon" className="size-10" />
      <h1 className="text-lg">{t("recover.wordsTitle")}</h1>
      <p className="mb-4 w-full text-center text-sm font-light text-secondary">
        {t("recover.wordsDescription")}
      </p>
      <ScrollArea className="h-24 md:h-28">
        <ul className="mb-4 flex flex-wrap items-center justify-center gap-4 font-normal">
          {words.map((word, index) => (
            <li key={`${word}-${index}`} className="flex items-center gap-1">
              {index + 1}.{" "}
              <span className="rounded-sm border border-foreground/20 px-2 font-light">
                {word}
              </span>
            </li>
          ))}
        </ul>
      </ScrollArea>
      <div className="mt-2 flex items-center justify-center gap-10 text-secondary">
        <span onClick={handleCopy} className="hover:cursor-pointer">
          <Copy className="mr-2 inline size-4" />
          {t("copy")}
        </span>
        <span onClick={handleDownload} className="hover:cursor-pointer">
          <Download className="mr-2 inline size-4" />
          {t("download")}
        </span>
      </div>
    </div>
  );
}

function WordsLoader({
  resource,
  updateData,
}: {
  resource: ReturnType<typeof createMnemonicResource>;
  updateData?: (data: WordsProps) => void;
}) {
  const words = resource.read();

  const updatedRef = useRef(false);

  useEffect(() => {
    if (!updatedRef.current && words && words.length > 0) {
      updateData?.({ words });
      updatedRef.current = true;
    }
  }, [words, updateData]);

  return <Words words={words} />;
}

export default function WordsStep({
  onNext,
  onClose,
  data,
  updateData,
}: FormStepProps<WordsProps>) {
  const providedWords = data?.words ?? [];

  // ONE predicate drives both decisions. Anything that is not a complete
  // phrase is not something the confirm step can validate, so it must be
  // regenerated AND must not let the user advance. Using different thresholds
  // for "regenerate" and "enable Next" would strand a partial list on screen
  // behind a permanently dead button.
  const hasFullPhrase = providedWords.length === PHRASE_WORDS_COUNT;

  // Bumped by the error boundary's retry. The boundary clears its OWN error
  // state, but that alone re-renders it with the same `children` element,
  // still closed over the errored resource. This state update is what
  // re-renders WordsStep so a fresh resource is built.
  const [, setResetNonce] = useState(0);
  const mnemonicRef = useRef<ReturnType<typeof createMnemonicResource>>();

  if (!hasFullPhrase && !mnemonicRef.current) {
    mnemonicRef.current = createMnemonicResource(PHRASE_ENTROPY);
  }

  const handleReset = useCallback(() => {
    mnemonicRef.current = undefined;
    setResetNonce((nonce) => nonce + 1);
  }, []);

  return (
    <WordsStepContainer
      onNext={onNext}
      onClose={onClose}
      nextDisabled={!hasFullPhrase}
    >
      {!hasFullPhrase ? (
        // `Suspense` only catches thrown promises, so a REJECTED dynamic
        // import (offline, or a stale chunk after a deploy) would otherwise
        // propagate out of this step and blow up whatever boundary is above it.
        <ErrorBoundary
          scope="panel"
          onReset={handleReset}
          fallback={(props) => <ErrorFallback {...props} />}
        >
          <Suspense fallback={<Spinner />}>
            {mnemonicRef.current && (
              <WordsLoader
                resource={mnemonicRef.current}
                updateData={updateData}
              />
            )}
          </Suspense>
        </ErrorBoundary>
      ) : (
        <Words words={providedWords} />
      )}
    </WordsStepContainer>
  );
}
